import { variableLabel, type TemplateParameterFormat } from "@school-sales-ai/shared";

/**
 * One example per variable found in the body.
 *
 * Meta requires an example for every variable and names a missing one as the
 * leading cause of rejection, so the rows are derived from the text rather than
 * added by hand — an admin cannot forget a variable they never had to notice.
 * The variables are computed once by the editor and handed down, so the body is
 * parsed once per render instead of once per component that cares.
 */
export function VariableExamplesTable({
  variables,
  format,
  examples,
  onChange,
  error,
}: {
  variables: string[];
  format: TemplateParameterFormat;
  examples: string[];
  onChange: (next: string[]) => void;
  error?: string;
}): React.JSX.Element | null {
  if (variables.length === 0) {
    return (
      <p className="text-xs text-ink-500">
        Este mensaje no tiene datos que cambien. Usa los botones bajo el cuerpo para
        insertar uno.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-ink-800">¿Qué va en cada dato?</label>
        <span className="text-xs text-ink-500">Obligatorios para Meta</span>
      </div>
      <div className="space-y-2">
        {variables.map((variable, index) => (
          <div key={variable} className="flex items-center gap-3">
            {/* A named template gets prose; a numbered one keeps the `{{1}}` chip,
                which is literally what that template contains. */}
            <span
              className={
                format === "NAMED"
                  ? "w-24 shrink-0 truncate text-xs font-medium text-ink-700"
                  : "w-16 shrink-0 rounded bg-ink-100 px-2 py-1 text-center font-mono text-xs text-ink-700"
              }
              title={format === "NAMED" ? variableLabel(variable) : undefined}
            >
              {format === "NAMED" ? variableLabel(variable) : `{{${variable}}}`}
            </span>
            <input
              type="text"
              value={examples[index] ?? ""}
              maxLength={120}
              aria-label={`Ejemplo de ${format === "NAMED" ? variableLabel(variable) : `{{${variable}}}`}`}
              onChange={(e) => {
                const next = [...examples];
                // Keep the array dense: the examples are read positionally
                // against the variables, so a hole would silently shift every
                // later variable's example.
                while (next.length < variables.length) next.push("");
                next[index] = e.target.value;
                onChange(next.slice(0, variables.length));
              }}
              placeholder="Ejemplo de lo que irá aquí"
              className="w-full rounded-md border border-ink-200 px-3 py-1.5 text-sm"
            />
          </div>
        ))}
      </div>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
